<template>
  <b-container class="iva-root resizable-columns">
    <b-card no-body>
      <b-card-header class="border-bottom-0">
        <div class="d-flex align-items-center justify-content-between">
          <slot name="header(left)" />
          <div class="ml-auto">
            <BaseDataTableSearchInput />
          </div>
        </div>
      </b-card-header>

      <b-card-body>
        <BaseDataTableFilters />

        <b-table
          ref="tableRef"
          :fields="tableFields"
          :items="itemsProviderWrapper"
          :filter="search"
          :current-page="currentPage"
          :per-page="pageSize"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          class="mb-0 iva-table"
          no-sort-reset
          responsive
          show-empty
          small
        >
          <template v-if="selectable" v-slot:head(select)>
            <slot name="head(select)" />
          </template>
          <template v-if="selectable" v-slot:cell(select)="row">
            <slot name="cell(select)" v-bind="row" />
          </template>

          <template v-slot:head(expand)>
            <span class="sr-only">Expand</span>
          </template>
          <template v-slot:cell(expand)="row">
            <button
              type="button"
              class="iva-expand-btn"
              :aria-expanded="row.detailsShowing"
              @click="() => { row.toggleDetails(); $emit('row-toggled', row.item) }"
            >
              <b-icon :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'" />
            </button>
          </template>

          <template v-for="column in columns" v-slot:[`head(${column.key})`]="head">
            <BaseDataTableTableHead :key="column.key" :column-name="column.key">
              <slot v-bind="head" :name="`head(${column.key})`" />

              <template v-slot:[`search(${column.key})`]="data">
                <slot v-bind="{ ...data, filters, addFilter }" :name="`search(${column.key})`" />
              </template>
            </BaseDataTableTableHead>
          </template>

          <template v-for="column in columns" v-slot:[`cell(${column.key})`]="cell">
            <slot v-bind="cell" :name="`cell(${column.key})`" />
          </template>

          <template v-slot:row-details="row">
            <slot name="row-detail" v-bind="row" />
          </template>
        </b-table>
      </b-card-body>

      <b-card-footer class="py-4">
        <div class="d-flex align-items-center justify-content-between">
          <BaseDataTablePageSizeSelect />
          <BaseDataTablePagination />
        </div>
      </b-card-footer>
    </b-card>
  </b-container>
</template>

<script>
// Hand-rolled sibling of BaseDataTable/BaseDataTableTable that adds
// bootstrap-vue's native row-details (accordion) support, which the
// shared BaseDataTable family doesn't expose. Deliberately does NOT
// modify those shared files (used elsewhere — CashCall, BDX tables) —
// instead this replicates BaseDataTable.vue's provide()/composable setup
// so the same filter/search/pagination chrome (BaseDataTableFilters,
// BaseDataTableSearchInput, BaseDataTablePageSizeSelect,
// BaseDataTablePagination, BaseDataTableTableHead) works unmodified here,
// giving the Invoice view identical filter/sort UX to Generate Invoices.
import { defineComponent, provide, toRefs, ref, computed, watch, onMounted, getCurrentInstance } from '@vue/composition-api'
import { isArray } from 'lodash-es'

import { BaseDataTableInjectionKey } from '@components/base/base-data-table/BaseDataTable.constants'
import useBaseDataTableSearch from '@components/base/base-data-table/useBaseDataTableSearch'
import useBaseDataTableFilters from '@components/base/base-data-table/useBaseDataTableFilters'
import useBaseDataTablePagination from '@components/base/base-data-table/useBaseDataTablePagination'
import useBaseDataTableSorting from '@components/base/base-data-table/useBaseDataTableSorting'
import { enableColumnResize } from '@components/base/base-data-table/useResizableColumns'

import BaseDataTableFilters from '@components/base/base-data-table/BaseDataTableFilters.vue'
import BaseDataTableSearchInput from '@components/base/base-data-table/BaseDataTableSearchInput.vue'
import BaseDataTablePageSizeSelect from '@components/base/base-data-table/BaseDataTablePageSizeSelect.vue'
import BaseDataTablePagination from '@components/base/base-data-table/BaseDataTablePagination.vue'
import BaseDataTableTableHead from '@components/base/base-data-table/BaseDataTableTableHead.vue'

export default defineComponent({
  name: 'InvoiceViewAccordionTable',
  components: {
    BaseDataTableFilters,
    BaseDataTableSearchInput,
    BaseDataTablePageSizeSelect,
    BaseDataTablePagination,
    BaseDataTableTableHead,
  },
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Function,
      required: true,
    },
    total: {
      type: Number,
      default: 0,
    },
    initialPageSize: {
      type: Number,
      default: 25,
    },
    pageSizeOptions: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
    // Adds a leading checkbox column, rendered by the caller's
    // head(select)/cell(select) slots — selection state itself lives with
    // the caller (see useInvoicingSelection.js).
    selectable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['row-toggled'],
  setup(props) {
    const instance = getCurrentInstance()
    const tableId = `iva${instance.proxy._uid}`

    const { search, setSearch } = useBaseDataTableSearch()
    const { filters, addFilter, removeFilter } = useBaseDataTableFilters()
    const { currentPage, setCurrentPage, pageSize, setPageSize } = useBaseDataTablePagination(props.initialPageSize)
    const { sortBy, setSortBy, sortDesc, toggleSortDesc, sortDirection, toggleSortDirection } = useBaseDataTableSorting()

    const { columns, items, total, pageSizeOptions } = toRefs(props)

    const tableFields = computed(() => [
      ...(props.selectable ? [{ key: 'select', label: '', sortable: false, searchable: false, thClass: 'iva-narrow' }] : []),
      { key: 'expand', label: '', sortable: false, searchable: false, thClass: 'iva-narrow' },
      ...columns.value,
    ])

    const tableRef = ref(null)

    // Mirrors BaseDataTableTable.vue's own items-provider wrapper: a
    // range-picker filter's value is a [from, to] array — split into
    // `${key}_gt`/`${key}_lt` before handing off to the caller's
    // provider, same convention the shared component already uses.
    const itemsProviderWrapper = async (context) => {
      const providerFilters = filters.value
        .map((filter) => {
          if (!isArray(filter.value)) return filter
          return [
            { key: `${filter.key}_gt`, value: filter.value[0] },
            { key: `${filter.key}_lt`, value: filter.value[1] },
          ]
        })
        .flat()

      const results = await items.value({ ...context, filters: providerFilters })
      return results || []
    }

    // Without this, applying/clearing a per-column filter updates
    // `filters` but nothing tells <b-table> to actually re-invoke
    // itemsProviderWrapper — the table just silently keeps showing
    // whatever it last fetched. BaseDataTableTable.vue (the shared
    // component this forks) already has this; it was missed when this
    // fork was first built.
    watch(filters, () => {
      if (!tableRef.value) return
      tableRef.value.refresh()
    })

    // Re-fetches the current page — called by the owning view once a bulk
    // action has changed rows in place (archive, payment, received).
    const refresh = () => {
      if (tableRef.value) tableRef.value.refresh()
    }

    // bootstrap-vue renders <thead> synchronously from tableFields (only
    // <tbody> waits on itemsProviderWrapper), so the header cells this
    // attaches resize handles to already exist by the time this runs.
    onMounted(() => {
      enableColumnResize(instance.proxy.$el)
    })

    provide(BaseDataTableInjectionKey, {
      tableId,
      columns,
      total,

      search,
      setSearch,

      filters,
      addFilter,
      removeFilter,

      currentPage,
      setCurrentPage,

      pageSize,
      setPageSize,
      pageSizeOptions,

      sortBy,
      setSortBy,

      sortDesc,
      toggleSortDesc,

      sortDirection,
      toggleSortDirection,
    })

    return {
      columns,

      filters,
      addFilter,

      search,

      currentPage,
      pageSize,

      sortBy,
      setSortBy,
      sortDesc,
      sortDirection,

      tableFields,
      tableRef,
      refresh,

      itemsProviderWrapper,
    }
  },
})
</script>

<style scoped>
/* Matches Generate Invoices' (PendingInvoicesTable/ApprovedDispatchedInvoicesTable)
   .gi-root table styling exactly — same font sizes, header treatment,
   wrapping, and first-column (here: the expand chevron, there: the
   selection checkbox) width/padding. Cells here aren't wrapped in the
   BaseDataTableTableCell <div> those tables get from BaseDataTable, so
   padding is applied straight to td instead of `td > div`. */
.iva-root >>> .card .card-body {
  padding: 0 1.5rem !important;
}
.iva-root >>> .container {
  padding: 0 !important;
  max-width: initial !important;
}

.iva-table {
  table-layout: fixed;
}
.iva-table >>> th {
  font-size: 10px !important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 8px 6px;
  vertical-align: middle;
}
/* A value wider than the column's current width never wraps onto a
   second line (which would grow the row) — it's clipped at the column's
   current width instead. Widening the column (see
   useResizableColumns.js) is the only way to reveal the rest of it;
   there's no hover reveal. */
.iva-table >>> td {
  font-size: 11px !important;
  white-space: nowrap;
  overflow: hidden;
  vertical-align: middle;
  padding: 8px 10px;
}
/* Same row height as the Itemized view (BaseDataTable cells: 8px padding
   around one 16.5px line). Fixed rather than padding-derived so the taller
   controls in these rows — checkbox, status badge, actions button — sit
   centred inside it instead of each pushing the row taller. Only the main
   table's own rows — not the expanded accordion's row (.b-table-details)
   or the charges table nested inside it. */
.iva-table >>> table.b-table > tbody > tr:not(.b-table-details) > td {
  height: 33px;
  padding-top: 0;
  padding-bottom: 0;
}
.iva-table >>> th:first-child,
.iva-table >>> td:first-child {
  padding-left: 4px !important;
  padding-right: 4px !important;
}
/* Selection checkbox + expand chevron: just wide enough for the control. */
.iva-table >>> th.iva-narrow {
  width: 32px;
}

.iva-expand-btn {
  border: none;
  background: transparent;
  color: #1c4a50;
  cursor: pointer;
  padding: 2px 4px;
  line-height: 1;
}
.iva-expand-btn:hover { color: #123236; }
</style>
